import React from "react";
import { motion } from "framer-motion";
import { cva, type VariantProps } from "class-variance-authority";

const buttonVariants = cva(
  "gap-2 inline-flex items-center justify-center rounded-md font-bold transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-tiber-900 text-romance-50 hover:bg-tiber-950 focus:ring-tiber-900 shadow-[inset_0_-2px_4px_rgba(0,0,0,0.2)] hover:shadow-[inset_0_-3px_6px_rgba(0,0,0,0.25)]",
        secondary:
          "border-2 border-tiber-900 text-tiber-900 bg-transparent hover:bg-tiber-900 hover:text-romance-50 focus:ring-tiber-900",
        dorado:
          "bg-dorado text-white hover:bg-dorado-dark focus:ring-dorado shadow-[inset_0_-2px_4px_rgba(0,0,0,0.2)] hover:shadow-[inset_0_-3px_6px_rgba(0,0,0,0.25)]",
        ghost: "text-juridico hover:bg-nieve-dark focus:ring-juridico",
        danger:
          "bg-red-600 text-white hover:bg-red-700 focus:ring-red-600 shadow-[inset_0_-2px_4px_rgba(0,0,0,0.2)] hover:shadow-[inset_0_-3px_6px_rgba(0,0,0,0.25)]",
      },
      size: {
        sm: "px-3 py-1.5 text-sm",
        md: "px-6 py-3 text-base",
        lg: "px-8 py-4 text-lg",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      | "onDrag"
      | "onDragEnd"
      | "onDragStart"
      | "onAnimationStart"
      | "onAnimationEnd"
      | "onAnimationIteration"
    >,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      isLoading,
      leftIcon,
      rightIcon,
      children,
      disabled,
      onClick,
      type = "button",
      ...props
    },
    ref
  ) => {
    return (
      <motion.button
        type={type}
        className={buttonVariants({ variant, size, fullWidth, className })}
        ref={ref}
        disabled={disabled || isLoading}
        whileHover={{ scale: disabled || isLoading ? 1 : 1.02 }}
        whileTap={{ scale: disabled || isLoading ? 1 : 0.98 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        onClick={(e) => {
          if (onClick) {
            // Solo prevenir si el tipo es button (no submit)
            if (type === "button") {
              e.preventDefault();
              e.stopPropagation();
            }
            onClick(e);
          }
        }}
        {...(props as Omit<
          ButtonProps,
          | "className"
          | "variant"
          | "size"
          | "fullWidth"
          | "isLoading"
          | "leftIcon"
          | "rightIcon"
          | "children"
          | "disabled"
          | "onClick"
          | "type"
        >)}
      >
        {isLoading && (
          <motion.svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </motion.svg>
        )}
        {!isLoading && leftIcon && (
          <motion.span
            className="mr-2"
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            {leftIcon}
          </motion.span>
        )}
        {children}
        {!isLoading && rightIcon && (
          <motion.span
            className="ml-2"
            initial={{ opacity: 0, x: 4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            {rightIcon}
          </motion.span>
        )}
      </motion.button>
    );
  }
);

Button.displayName = "Button";
